import { ReactNode, useMemo } from 'react';

import createSafeContext from '@paradigm/utils/src/createSafeContext';

import { useDrfqRfq } from '../rfq-context';
import useSubmitAggressOrder from './useSubmitAggressOrder';
import { Report, Status, SubmitCallback } from './types';
import type { Order, RfqOrder } from '../../entities/drfq/orders';

export interface AggressAction {
  readonly submitAggressOrder: (cb: SubmitCallback<Report>) => void;
  readonly resetConfirmState: () => void;
  readonly isButtonEnabled: boolean;
  readonly status: Status;
  readonly lockedOrder?: RfqOrder;
}

export const [useAggressBuyAction, AggressBuyActionProvider] =
  createSafeContext<AggressAction>('AggressBuyAction');
export const [useAggressSellAction, AggressSellActionProvider] =
  createSafeContext<AggressAction>('AggressSellAction');
export const [useBid, BidProvider] = createSafeContext<RfqOrder | undefined>(
  'Bid',
);
export const [useAsk, AskProvider] = createSafeContext<RfqOrder | undefined>(
  'Ask',
);

function useAggressForm(side: Order['side'], order?: RfqOrder): AggressAction {
  const rfq = useDrfqRfq();
  const { submitAggressOrder, status, lockedOrder, resetConfirmState } =
    useSubmitAggressOrder({
      rfq,
      order,
      side,
    });

  const view = useMemo(() => {
    const isOrderEmpty = order === undefined;

    return {
      status,
      lockedOrder,
      isButtonEnabled:
        !isOrderEmpty && !['submitting', 'loading'].includes(status),
    };
  }, [order, status, lockedOrder]);

  const actions = useMemo(
    () => ({
      submitAggressOrder,
      resetConfirmState,
    }),
    [resetConfirmState, submitAggressOrder],
  );

  return { ...view, ...actions };
}

interface Props {
  children: ReactNode;
}

export function AggressBuyProvider({ children }: Props) {
  const ask = useAsk();
  const value: AggressAction = useAggressForm('BUY', ask);

  return (
    <AggressBuyActionProvider value={value}>
      {children}
    </AggressBuyActionProvider>
  );
}

export function AggressSellProvider({ children }: Props) {
  const bid = useBid();
  const value: AggressAction = useAggressForm('SELL', bid);

  return (
    <AggressSellActionProvider value={value}>
      {children}
    </AggressSellActionProvider>
  );
}
